import { useRef, useState, type DragEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { Check, CloudUpload, X } from 'lucide-react'
import { detailMessage } from '@/api/client'
import { useMeta } from '@/api/queries'
import type { UploadVisibility } from '@/api/types'
import { cn } from '@/lib/cn'
import { spring } from '@/lib/motion'
import { formatBytes, uploadFile } from '@/lib/upload'
import { useUiStore } from '@/stores/uiStore'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Dialog'
import { TextField } from '@/components/ui/Field'
import { IconButton } from '@/components/ui/IconButton'
import { Segmented } from '@/components/ui/Segmented'
import { DepartmentField } from './DepartmentField'
import { FileBadge } from './FileBadge'

const ACCEPTED = [
  '.pdf',
  '.docx',
  '.pptx',
  '.xlsx',
  '.xlsm',
  '.csv',
  '.md',
  '.txt',
  '.html',
  '.htm',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
]

type Item = {
  key: string
  file: File
  progress: number
  state: 'queued' | 'uploading' | 'done' | 'error'
  error?: string
}

const extensionOf = (name: string) => (name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '')

export function UploadDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const meta = useMeta()
  const client = useQueryClient()
  const announce = useUiStore((s) => s.announce)
  const maxMb = meta.features.max_upload_mb ?? 50
  const isAdmin = Boolean(meta.user?.is_admin)
  const inputRef = useRef<HTMLInputElement>(null)
  const [items, setItems] = useState<Item[]>([])
  const [title, setTitle] = useState('')
  const [department, setDepartment] = useState('')
  const [visibility, setVisibility] = useState<UploadVisibility>('everyone')
  const [dragging, setDragging] = useState(false)
  const [running, setRunning] = useState(false)

  const reset = () => {
    setItems([])
    setTitle('')
    setDepartment('')
    setVisibility('everyone')
  }

  const add = (files: FileList | File[]) => {
    const next: Item[] = Array.from(files).map((file) => {
      const ext = extensionOf(file.name)
      let error: string | undefined
      if (!ACCEPTED.includes(ext)) error = `${ext || 'This file type'} isn’t supported.`
      else if (file.size > maxMb * 1024 * 1024) error = `Larger than the ${maxMb} MB limit.`
      else if (file.size === 0) error = 'The file is empty.'
      return {
        key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        progress: 0,
        state: error ? 'error' : 'queued',
        error,
      }
    })
    setItems((current) => [...current.filter((i) => i.state !== 'done'), ...next])
  }

  const update = (key: string, patch: Partial<Item>) =>
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)))

  const queued = items.filter((i) => i.state === 'queued')
  const single = items.length === 1

  const start = async () => {
    setRunning(true)
    let ok = 0
    for (const item of queued) {
      update(item.key, { state: 'uploading', progress: 0 })
      try {
        await uploadFile(
          item.file,
          { title: single ? title.trim() : undefined, department: department || null, visibility },
          (progress) => update(item.key, { progress }),
        )
        update(item.key, { state: 'done', progress: 1 })
        ok += 1
      } catch (error) {
        update(item.key, { state: 'error', error: detailMessage(error) })
      }
    }
    setRunning(false)
    void client.invalidateQueries({ queryKey: ['uploads'] })
    void client.invalidateQueries({ queryKey: ['library'] })
    if (ok) announce(`${ok} ${ok === 1 ? 'file' : 'files'} uploaded. Indexing has started.`)
    if (ok === queued.length) {
      window.setTimeout(() => {
        onOpenChange(false)
        reset()
      }, 700)
    }
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    if (event.dataTransfer.files.length) add(event.dataTransfer.files)
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (running) return
        onOpenChange(next)
        if (!next) reset()
      }}
      title="Upload documents"
      description="Files are read, indexed and become answerable in chat within a minute or two."
    >
      <motion.button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        animate={{ scale: dragging ? 1.015 : 1 }}
        transition={spring.snappy}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-[1.5px] border-dashed px-6 py-8 text-center transition-colors duration-200',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          dragging ? 'border-accent bg-accent-soft/60' : 'border-line-strong bg-ink/[0.02] hover:bg-ink/[0.04]',
        )}
      >
        <motion.span
          animate={{ y: dragging ? -4 : 0 }}
          transition={spring.snappy}
          className="flex size-12 items-center justify-center rounded-full bg-accent-soft text-accent-text"
        >
          <CloudUpload className="size-6" aria-hidden />
        </motion.span>
        <span className="text-[15px] font-medium text-ink">
          {dragging ? 'Drop to add' : 'Drag files here or browse'}
        </span>
        <span className="text-xs text-ink-faint">
          PDF, Word, PowerPoint, Excel, CSV, Markdown, text, HTML or images · up to {maxMb} MB
        </span>
      </motion.button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED.join(',')}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(event) => {
          if (event.target.files) add(event.target.files)
          event.target.value = ''
        }}
      />

      {items.length > 0 && (
        <ul aria-label="Files to upload" className="mt-4 space-y-2">
          <AnimatePresence initial={false}>
            {items.map((item) => (
              <motion.li
                key={item.key}
                layout
                initial={{ opacity: 0, y: 8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: -12, transition: { duration: 0.15 } }}
                transition={spring.smooth}
                className="relative flex items-center gap-3 overflow-hidden rounded-xl bg-ink/[0.03] px-3 py-2.5 dark:bg-white/[0.04]"
              >
                <FileBadge fileType={extensionOf(item.file.name).slice(1)} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-medium text-ink">{item.file.name}</p>
                  <p className={cn('truncate text-xs', item.state === 'error' ? 'text-danger' : 'text-ink-faint')}>
                    {item.state === 'error'
                      ? item.error
                      : item.state === 'uploading'
                        ? `Uploading… ${Math.round(item.progress * 100)}%`
                        : item.state === 'done'
                          ? 'Uploaded — indexing'
                          : formatBytes(item.file.size)}
                  </p>
                </div>
                {item.state === 'done' ? (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={spring.snappy}
                    className="flex size-6 items-center justify-center rounded-full bg-success text-white"
                  >
                    <Check className="size-3.5" strokeWidth={3} aria-label="Uploaded" />
                  </motion.span>
                ) : (
                  !running && (
                    <IconButton
                      label={`Remove ${item.file.name}`}
                      size="xs"
                      tooltip={false}
                      onClick={() => setItems((current) => current.filter((i) => i.key !== item.key))}
                    >
                      <X />
                    </IconButton>
                  )
                )}
                {item.state === 'uploading' && (
                  <motion.span
                    className="absolute inset-x-0 bottom-0 h-[2px] origin-left bg-accent"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: item.progress }}
                    transition={{ duration: 0.2 }}
                    aria-hidden
                  />
                )}
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}

      <div className="mt-5 space-y-4">
        {single && (
          <TextField
            label="Title"
            placeholder={items[0]?.file.name.replace(/\.[^.]+$/, '')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            hint="Optional. Shown in answers and the library."
          />
        )}
        <DepartmentField value={department} onChange={setDepartment} />
        {isAdmin && (
          <div>
            <p className="mb-1.5 text-[13px] font-medium text-ink-muted">Who can see it</p>
            <Segmented<UploadVisibility>
              label="Who can see it"
              value={visibility}
              onChange={setVisibility}
              options={[
                { value: 'everyone', label: 'Everyone' },
                { value: 'admins', label: 'Admins only' },
              ]}
            />
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={running}
          onClick={() => {
            onOpenChange(false)
            reset()
          }}
        >
          {items.some((i) => i.state === 'done') ? 'Done' : 'Cancel'}
        </Button>
        <Button size="sm" disabled={running || queued.length === 0} onClick={() => void start()}>
          {running ? 'Uploading…' : queued.length > 1 ? `Upload ${queued.length} files` : 'Upload'}
        </Button>
      </div>
    </Modal>
  )
}
